import { Graphics, Container, ContainerChild, Text } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetDateTime,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { TimerService } from "../Services/TimerService";

import { getClockDesign } from "../../../../data/clockDesignsCatalog";

import { ClockDesignRenderer } from "./ClockDesignRenderer";

import { formatDateTime } from "./DateTimeWidget.utils";

export class DateTimeWidgetRenderer {
  private textObjects = new Map<string, Text>();

  private updateCallbacks = new Map<string, (time: number) => void>();

  private decorationGraphics = new Map<string, Graphics>();

  private lastTextWidths = new Map<string, number>();

  // One ClockDesignRenderer per widget id, when options.designId is set.
  private designRenderers = new Map<string, ClockDesignRenderer>();

  render(
    widget: IStudioWidget,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const options = (widget as IStudioWidgetDateTime).options ?? {};

    // Tear down whatever this widget id rendered last time (design sprite or
    // legacy text callback) so switching design<->legacy never leaks a
    // TimerService subscription or a stale texture.
    this.cleanupWidget(widget.id);

    // Design-store branch: when a design is selected, render it as a live
    // sprite and skip the legacy text path entirely.
    const design = getClockDesign(options.designId);

    if (design) {
      const designRenderer = new ClockDesignRenderer(design);

      this.designRenderers.set(widget.id, designRenderer);

      designRenderer.render(widget, container, destination);

      return;
    }

    const backgroundColor = options.backgroundColor || "transparent";

    // Background

    if (backgroundColor !== "transparent") {
      const bg = new Graphics();

      bg.rect(0, 0, destination.width, destination.height).fill(
        backgroundColor,
      );

      container.addChild(bg);
    }

    // Mask for overflow hidden

    const mask = new Graphics();

    mask.rect(0, 0, destination.width, destination.height).fill(0xffffff);

    container.addChild(mask);

    container.mask = mask;

    // Font Styles

    const fontSize = options.fontSize ? parseInt(options.fontSize) : 24;

    const fontFamily = options.fontFamily || "Roboto";

    const color = options.fontColor || "#ffffff";

    const fontWeight = options.bold ? "bold" : "normal";

    const fontStyle = options.italic ? "italic" : "normal";

    const textAlign = options.textAlign || "center";

    const lineHeight = options.lineHeight
      ? fontSize * options.lineHeight
      : undefined;

    const wordSpacing = options.wordSpacing ? options.wordSpacing : 0;

    const hasUnderline = options.underline === true;

    const hasStrike = options.strike === true;

    const timerService = TimerService.getInstance();

    // Initial render

    const textContent = formatDateTime(new Date(), options);

    const text = new Text({
      text: textContent,

      style: {
        fontFamily,

        fontSize,

        fill: color,

        align: textAlign,

        fontWeight,

        fontStyle,

        ...(lineHeight !== undefined ? { lineHeight } : {}),

        wordWrapWidth: destination.width,

        letterSpacing: wordSpacing,
      },
    });

    // Position based on textAlign

    switch (textAlign) {
      case "left":
        text.anchor.set(0, 0.5);

        text.position.set(0, destination.height / 2);

        break;

      case "right":
        text.anchor.set(1, 0.5);

        text.position.set(destination.width, destination.height / 2);

        break;

      case "center":

      // falls through

      default:
        text.anchor.set(0.5, 0.5);

        text.position.set(destination.width / 2, destination.height / 2);

        break;
    }

    container.cullable = true;

    container.addChild(text as unknown as ContainerChild);

    // Decorations container (lines)

    const decorations = new Graphics();

    container.addChild(decorations);

    const widgetId = widget.id;

    const widgetKey = `datetime_${widgetId}`;

    this.textObjects.set(widgetKey, text);

    this.decorationGraphics.set(widgetKey, decorations);

    // Draw initial decorations

    this.drawDecorations(text, decorations, hasUnderline, hasStrike, color);

    // Clean up previous callback

    if (this.updateCallbacks.has(widgetKey)) {
      timerService.unsubscribe(this.updateCallbacks.get(widgetKey)!);
    }

    const updateCallback = (now: number) => {
      const formatted = formatDateTime(new Date(now), options);

      // Skip the Pixi Text setter (font measure + bounds recalc) when the
      // formatted string is identical to the previous tick.
      if (text.text === formatted) return;

      text.text = formatted;

      // Only redraw decorations if text width actually changed

      const currentWidth = text.width;

      if (currentWidth !== this.lastTextWidths.get(widgetKey)) {
        this.lastTextWidths.set(widgetKey, currentWidth);

        this.drawDecorations(text, decorations, hasUnderline, hasStrike, color);
      }
    };

    this.updateCallbacks.set(widgetKey, updateCallback);

    timerService.subscribe(updateCallback);
  }

  private drawDecorations(
    text: Text,

    graphics: Graphics,

    underline: boolean,

    strike: boolean,

    color: string,
  ) {
    if (!underline && !strike) return;

    graphics.clear();

    const width = text.width;

    const height = text.height;

    // Compute x based on anchor (which varies with textAlign)

    const anchorX = text.anchor.x;

    const anchorY = text.anchor.y;

    const x = text.x - width * anchorX;

    const y = text.y;

    // Ensure we use the same color

    graphics.stroke({
      width: Math.max(2, (text.style.fontSize as number) / 10),

      color,
    });

    if (underline) {
      const lineY = y + height * (1 - anchorY);

      graphics.moveTo(x, lineY);

      graphics.lineTo(x + width, lineY);
    }

    if (strike) {
      const strikeY = y + height * (0.5 - anchorY);

      graphics.moveTo(x, strikeY);

      graphics.lineTo(x + width, strikeY);
    }
  }

  /**
   * Tear down a single widget id's render state — the legacy text callback and
   * any active ClockDesignRenderer — so a re-render (e.g. switching design)
   * starts clean without leaking TimerService subscriptions or GPU textures.
   */
  private cleanupWidget(widgetId: string) {
    const widgetKey = `datetime_${widgetId}`;

    const callback = this.updateCallbacks.get(widgetKey);

    if (callback) {
      TimerService.getInstance().unsubscribe(callback);

      this.updateCallbacks.delete(widgetKey);
    }

    this.textObjects.delete(widgetKey);

    this.decorationGraphics.delete(widgetKey);

    this.lastTextWidths.delete(widgetKey);

    const designRenderer = this.designRenderers.get(widgetId);

    if (designRenderer) {
      designRenderer.destroy();

      this.designRenderers.delete(widgetId);
    }
  }

  public destroy() {
    const timerService = TimerService.getInstance();

    for (const [_key, callback] of this.updateCallbacks.entries()) {
      timerService.unsubscribe(callback);
    }

    for (const designRenderer of this.designRenderers.values()) {
      designRenderer.destroy();
    }

    this.designRenderers.clear();

    this.updateCallbacks.clear();

    this.textObjects.clear();

    this.decorationGraphics.clear();

    this.lastTextWidths.clear();
  }
}
